import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const legacyYabijiText = "台風は過ぎましたが、海況がまだ不安定です。再開しているツアーもあるので、各ツアー会社に直接ご予約ください！台風明けの海はものすごく綺麗でおすすめです。";
const previousDefaultYabijiText = "各ツアー会社に直接お問い合わせ・ご予約ください！";
const previousSeasonEndingYabijiText = "今季の八重干瀬ツアーは、まもなく終了予定です。来季は3月ごろから再開予定。詳しい開催時期・ご予約は、各ツアー会社へ直接お問い合わせください。";
const previousCurrentSeasonEndingYabijiText = "今シーズンの八重干瀬ツアーは、もうすぐ終了となります。";
const defaultYabijiText = "今シーズンの八重干瀬ツアーは、もうすぐ終わりです。来年の3月ごろからまた船が出るので、各ツアー会社のSNSをチェックしてください。";
const legacyTopAlertText = "台風が発生するかもしれません。発生した場合、シルバーウィークの沖縄に影響が出る予想です。";
const previousTopAlertText = "熱帯低気圧が発生。シルバーウィークの宮古島に影響が出る見込みです😭";
const defaultTopAlertText = "台風30号の影響は、10/20（火）ごろからうねりや風が強まる可能性があります。最新の進路と海況を追って報告します。";

export const OCEAN_GUIDE_SETTINGS_PATH = ["oceanGuide", "settings"];

export const oceanGuideAdminTextKeys = [
  "topAlertText",
  "todayTitle",
  "tomorrowTitle",
  "todayAssessmentText",
  "tomorrowAssessmentText",
  "todaySunsetText",
  "tomorrowSunsetText",
  "todayNote",
  "tomorrowNote",
  "yabijiText",
  "todayWeatherSummary",
  "tomorrowWeatherSummary",
  "dayAfterTomorrowAssessmentText",
  "dayAfterTomorrowWeatherSummary",
  "todayYabijiText",
  "tomorrowYabijiText",
  ...["today", "tomorrow", "dayAfterTomorrow"].flatMap((day) =>
    ["Drone", "Snorkel", "Marine"].flatMap((kind) => [
      `${day}${kind}Time`,
      `${day}${kind}Text`,
    ]),
  ),
];

const emptyAdminText = Object.fromEntries(
  oceanGuideAdminTextKeys.map((key) => [key, ""]),
);

const defaultTranslations = {
  EN: { ...emptyAdminText },
  ZH: { ...emptyAdminText },
  KO: { ...emptyAdminText },
};

export const defaultOceanGuideSettings = {
  topAlertEnabled: true,
  forecastTyphoonVisible: true,
  todayRating: "auto",
  tomorrowRating: "auto",
  todayWeather: "auto",
  tomorrowWeather: "auto",
  dayAfterTomorrowWeather: "auto",
  todayClarity: "auto",
  tomorrowClarity: "auto",
  dayAfterTomorrowRating: "auto",
  dayAfterTomorrowClarity: "auto",
  todayNote: "",
  tomorrowNote: "",
  todayAssessmentText: "",
  tomorrowAssessmentText: "",
  todayTitle: "",
  tomorrowTitle: "",
  todayScores: { snorkel: -1, marine: -1, photo: -1 },
  tomorrowScores: { snorkel: -1, marine: -1, photo: -1 },
  dayAfterTomorrowScores: { snorkel: -1, marine: -1, photo: -1 },
  todaySunsetRating: "auto",
  tomorrowSunsetRating: "auto",
  todaySunsetText: "",
  tomorrowSunsetText: "",
  todayStarRating: "auto",
  tomorrowStarRating: "auto",
  todayBestTimeRatings: { drone: "auto", snorkel: "auto", marine: "auto" },
  tomorrowBestTimeRatings: { drone: "auto", snorkel: "auto", marine: "auto" },
  dayAfterTomorrowBestTimeRatings: { drone: "auto", snorkel: "auto", marine: "auto" },
  ...Object.fromEntries(oceanGuideAdminTextKeys.map((key) => [key, ""])),
  topAlertText: defaultTopAlertText,
  yabijiText: defaultYabijiText,
  translations: defaultTranslations,
};

const firestoreLocaleKey = { EN: "EN", 中文: "ZH", 한국어: "KO" };

const getJstDayNumber = (value) => {
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type) => Number(parts.find((item) => item.type === type)?.value);
  return Math.floor(Date.UTC(part("year"), part("month") - 1, part("day")) / 86400000);
};

const rollDayWords = (value = "", locale = "JP") => {
  if (locale === "EN") return value
    .replaceAll("The day after tomorrow", "Today")
    .replaceAll("the day after tomorrow", "today")
    .replaceAll("Tomorrow", "Today")
    .replaceAll("tomorrow", "today");
  if (locale === "ZH") return value.replaceAll("后天", "今天").replaceAll("明天", "今天");
  if (locale === "KO") return value.replaceAll("모레", "오늘").replaceAll("내일", "오늘");
  return value.replaceAll("明後日", "今日").replaceAll("あさって", "今日").replaceAll("明日", "今日");
};

const rollDayWordsToTomorrow = (value = "", locale = "JP") => {
  if (locale === "EN") return value
    .replaceAll("The day after tomorrow", "Tomorrow")
    .replaceAll("the day after tomorrow", "tomorrow");
  if (locale === "ZH") return value.replaceAll("后天", "明天");
  if (locale === "KO") return value.replaceAll("모레", "내일");
  return value.replaceAll("明後日", "明日").replaceAll("あさって", "明日");
};

const shiftLocalizedTexts = (translations, elapsedDays) => Object.fromEntries(
  Object.entries(translations).map(([locale, text]) => [locale, {
    ...text,
    todayTitle: elapsedDays === 1 ? rollDayWords(text.tomorrowTitle, locale) : "",
    todayAssessmentText: elapsedDays === 1 ? rollDayWords(text.tomorrowAssessmentText, locale) : elapsedDays === 2 ? text.dayAfterTomorrowAssessmentText : "",
    todayWeatherSummary: elapsedDays === 1 ? rollDayWords(text.tomorrowWeatherSummary, locale) : elapsedDays === 2 ? text.dayAfterTomorrowWeatherSummary : "",
    tomorrowAssessmentText: elapsedDays === 1 ? rollDayWordsToTomorrow(text.dayAfterTomorrowAssessmentText, locale) : "",
    tomorrowWeatherSummary: elapsedDays === 1 ? rollDayWordsToTomorrow(text.dayAfterTomorrowWeatherSummary, locale) : "",
    todaySunsetText: elapsedDays === 1 ? rollDayWords(text.tomorrowSunsetText, locale) : "",
    todayNote: elapsedDays === 1 ? rollDayWords(text.tomorrowNote, locale) : "",
    todayYabijiText: elapsedDays === 1 ? rollDayWords(text.tomorrowYabijiText, locale) : "",
    todayDroneTime: elapsedDays === 1 ? text.tomorrowDroneTime : elapsedDays === 2 ? text.dayAfterTomorrowDroneTime : "",
    todayDroneText: elapsedDays === 1 ? text.tomorrowDroneText : elapsedDays === 2 ? text.dayAfterTomorrowDroneText : "",
    todaySnorkelTime: elapsedDays === 1 ? text.tomorrowSnorkelTime : elapsedDays === 2 ? text.dayAfterTomorrowSnorkelTime : "",
    todaySnorkelText: elapsedDays === 1 ? text.tomorrowSnorkelText : elapsedDays === 2 ? text.dayAfterTomorrowSnorkelText : "",
    todayMarineTime: elapsedDays === 1 ? text.tomorrowMarineTime : elapsedDays === 2 ? text.dayAfterTomorrowMarineTime : "",
    todayMarineText: elapsedDays === 1 ? text.tomorrowMarineText : elapsedDays === 2 ? text.dayAfterTomorrowMarineText : "",
    tomorrowTitle: "",
    dayAfterTomorrowAssessmentText: "",
    dayAfterTomorrowWeatherSummary: "",
    tomorrowSunsetText: "",
    tomorrowNote: "",
    tomorrowYabijiText: "",
    tomorrowDroneTime: elapsedDays === 1 ? text.dayAfterTomorrowDroneTime : "",
    tomorrowDroneText: elapsedDays === 1 ? text.dayAfterTomorrowDroneText : "",
    tomorrowSnorkelTime: elapsedDays === 1 ? text.dayAfterTomorrowSnorkelTime : "",
    tomorrowSnorkelText: elapsedDays === 1 ? text.dayAfterTomorrowSnorkelText : "",
    tomorrowMarineTime: elapsedDays === 1 ? text.dayAfterTomorrowMarineTime : "",
    tomorrowMarineText: elapsedDays === 1 ? text.dayAfterTomorrowMarineText : "",
    dayAfterTomorrowDroneTime: "",
    dayAfterTomorrowDroneText: "",
    dayAfterTomorrowSnorkelTime: "",
    dayAfterTomorrowSnorkelText: "",
    dayAfterTomorrowMarineTime: "",
    dayAfterTomorrowMarineText: "",
  }]),
);

const rollSettingsToToday = (settings) => {
  const savedDay = getJstDayNumber(settings.updatedAt);
  const today = getJstDayNumber(new Date());
  const elapsedDays = savedDay === null || today === null ? 0 : today - savedDay;
  if (elapsedDays <= 0) return settings;

  const todayScores = elapsedDays === 1
    ? settings.tomorrowScores
    : elapsedDays === 2
      ? settings.dayAfterTomorrowScores
      : { snorkel: -1, marine: -1, photo: -1 };

  return {
    ...settings,
    todayRating: elapsedDays === 1 ? settings.tomorrowRating : elapsedDays === 2 ? settings.dayAfterTomorrowRating : "auto",
    todayWeather: elapsedDays === 1 ? settings.tomorrowWeather : elapsedDays === 2 ? settings.dayAfterTomorrowWeather : "auto",
    todayClarity: elapsedDays === 1 ? settings.tomorrowClarity : elapsedDays === 2 ? settings.dayAfterTomorrowClarity : "auto",
    todayScores,
    todaySunsetRating: elapsedDays === 1 ? settings.tomorrowSunsetRating : "auto",
    todayStarRating: elapsedDays === 1 ? settings.tomorrowStarRating : "auto",
    todayBestTimeRatings: elapsedDays === 1
      ? settings.tomorrowBestTimeRatings
      : elapsedDays === 2
        ? settings.dayAfterTomorrowBestTimeRatings
        : { drone: "auto", snorkel: "auto", marine: "auto" },
    todayTitle: elapsedDays === 1 ? rollDayWords(settings.tomorrowTitle) : "",
    todayAssessmentText: elapsedDays === 1 ? rollDayWords(settings.tomorrowAssessmentText) : elapsedDays === 2 ? rollDayWords(settings.dayAfterTomorrowAssessmentText) : "",
    todayWeatherSummary: elapsedDays === 1 ? rollDayWords(settings.tomorrowWeatherSummary) : elapsedDays === 2 ? rollDayWords(settings.dayAfterTomorrowWeatherSummary) : "",
    todaySunsetText: elapsedDays === 1 ? rollDayWords(settings.tomorrowSunsetText) : "",
    todayNote: elapsedDays === 1 ? rollDayWords(settings.tomorrowNote) : "",
    todayYabijiText: elapsedDays === 1 ? rollDayWords(settings.tomorrowYabijiText) : "",
    todayDroneTime: elapsedDays === 1 ? settings.tomorrowDroneTime : "",
    todayDroneText: elapsedDays === 1 ? settings.tomorrowDroneText : "",
    todaySnorkelTime: elapsedDays === 1 ? settings.tomorrowSnorkelTime : "",
    todaySnorkelText: elapsedDays === 1 ? settings.tomorrowSnorkelText : "",
    todayMarineTime: elapsedDays === 1 ? settings.tomorrowMarineTime : "",
    todayMarineText: elapsedDays === 1 ? settings.tomorrowMarineText : "",
    tomorrowRating: elapsedDays === 1 ? settings.dayAfterTomorrowRating : "auto",
    tomorrowWeather: elapsedDays === 1 ? settings.dayAfterTomorrowWeather : "auto",
    tomorrowClarity: elapsedDays === 1 ? settings.dayAfterTomorrowClarity : "auto",
    tomorrowScores: elapsedDays === 1
      ? settings.dayAfterTomorrowScores
      : { snorkel: -1, marine: -1, photo: -1 },
    tomorrowSunsetRating: "auto",
    tomorrowStarRating: "auto",
    tomorrowBestTimeRatings: elapsedDays === 1
      ? settings.dayAfterTomorrowBestTimeRatings
      : { drone: "auto", snorkel: "auto", marine: "auto" },
    tomorrowTitle: "",
    tomorrowAssessmentText: elapsedDays === 1 ? rollDayWordsToTomorrow(settings.dayAfterTomorrowAssessmentText) : "",
    tomorrowWeatherSummary: elapsedDays === 1 ? rollDayWordsToTomorrow(settings.dayAfterTomorrowWeatherSummary) : "",
    tomorrowSunsetText: "",
    tomorrowNote: "",
    tomorrowYabijiText: "",
    tomorrowDroneTime: elapsedDays === 1 ? settings.dayAfterTomorrowDroneTime : "",
    tomorrowDroneText: elapsedDays === 1 ? settings.dayAfterTomorrowDroneText : "",
    tomorrowSnorkelTime: elapsedDays === 1 ? settings.dayAfterTomorrowSnorkelTime : "",
    tomorrowSnorkelText: elapsedDays === 1 ? settings.dayAfterTomorrowSnorkelText : "",
    tomorrowMarineTime: elapsedDays === 1 ? settings.dayAfterTomorrowMarineTime : "",
    tomorrowMarineText: elapsedDays === 1 ? settings.dayAfterTomorrowMarineText : "",
    dayAfterTomorrowRating: "auto",
    dayAfterTomorrowWeather: "auto",
    dayAfterTomorrowClarity: "auto",
    dayAfterTomorrowBestTimeRatings: { drone: "auto", snorkel: "auto", marine: "auto" },
    dayAfterTomorrowAssessmentText: "",
    dayAfterTomorrowWeatherSummary: "",
    dayAfterTomorrowDroneTime: "",
    dayAfterTomorrowDroneText: "",
    dayAfterTomorrowSnorkelTime: "",
    dayAfterTomorrowSnorkelText: "",
    dayAfterTomorrowMarineTime: "",
    dayAfterTomorrowMarineText: "",
    dayAfterTomorrowScores: { snorkel: -1, marine: -1, photo: -1 },
    translations: shiftLocalizedTexts(settings.translations, elapsedDays),
  };
};

export const getOceanGuideAdminText = (settings, key, lang = "JP") => {
  if (lang === "JP") {
    const value = settings?.[key] || "";
    if (
      key === "topAlertText" &&
      [legacyTopAlertText, previousTopAlertText].includes(value)
    ) return defaultTopAlertText;
    return [legacyYabijiText, previousDefaultYabijiText, previousSeasonEndingYabijiText, previousCurrentSeasonEndingYabijiText].includes(value) ? defaultYabijiText : value;
  }
  return settings?.translations?.[firestoreLocaleKey[lang]]?.[key] || "";
};

export const getSpotOverrideId = (name = "") => name.replaceAll("/", "／");

const getJstDateKey = (date = new Date()) => new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
}).format(date);

// Location ratings are intended as same-day operational overrides. Requiring an
// explicit date prevents yesterday's manual ◎/○ from silently replacing today's
// wind and wave based safety calculation. Legacy records without overrideDate are
// therefore treated as automatic.
export const getTodaySpotOverride = (override) => (
  override?.overrideDate === getJstDateKey() ? override : null
);

export const subscribeOceanGuideSettings = (onValue, onError) =>
  onSnapshot(
    doc(db, ...OCEAN_GUIDE_SETTINGS_PATH),
    (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      onValue(rollSettingsToToday({
        ...defaultOceanGuideSettings,
        ...data,
        todayYabijiText: Object.prototype.hasOwnProperty.call(data, "todayYabijiText")
          ? getOceanGuideAdminText({ todayYabijiText: data.todayYabijiText }, "todayYabijiText")
          : getOceanGuideAdminText({ yabijiText: data.yabijiText }, "yabijiText") || defaultOceanGuideSettings.yabijiText,
        translations: Object.fromEntries(
          Object.entries(defaultTranslations).map(([locale, defaults]) => [
            locale,
            {
              ...defaults,
              ...(data.translations?.[locale] || {}),
              todayYabijiText: Object.prototype.hasOwnProperty.call(
                data.translations?.[locale] || {},
                "todayYabijiText",
              )
                ? data.translations[locale].todayYabijiText
                : data.translations?.[locale]?.yabijiText || "",
            },
          ]),
        ),
      }));
    },
    onError,
  );

export const subscribeOceanGuideSpotOverrides = (onValue, onError) =>
  onSnapshot(
    collection(db, "oceanGuideSpotOverrides"),
    (snapshot) => {
      onValue(Object.fromEntries(snapshot.docs.map((item) => [item.id, item.data()])));
    },
    onError,
  );
